/// <amd-module name="Repos/Files/GitFilesService" />
import { ChangesFilesService } from "Repos/ChangesCommon/ChangesFilesService";
import { FileDiffItemModel, IChangesFilesService, IFileDiff, IFileDiffParameters } from "Repos/ChangesCommon/Contracts";
import { FileContentMetadata, GitItem, GitVersionDescriptor, VersionControlRecursionType } from "Repos/Common/Contracts/Git";
import { getGitResourceUri } from "Repos/Common/Util/Editor";
import { commitToVersionDescriptor, getVersionString, gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IGitItemContent } from "Repos/Files/Contracts";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssRestClient, IVssService, RestClients, Services } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";
import { combineUrlPaths, Uri } from "VSS/Platform/Util/Url";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IGitFilesService extends IVssService, IChangesFilesService<GitVersionDescriptor> {
    /**
     * Gets the file diff information for specified file.
     * @param fileDiffParameters File to fetch diff information.
     */
    getFileDiff(fileDiffParameters: IFileDiffParameters): IReadonlyObservableValue<IFileDiff | undefined>;

    /**
     * Gets the content information for the specified file. Returns a promise for the content.
     *
     * @param path File path
     * @param version version of the file
     */
    getItemContentPromise(path: string, version: GitVersionDescriptor): Promise<IGitItemContent>;
}

class GitFilesService extends ChangesFilesService<GitVersionDescriptor> implements IGitFilesService {
    private projectId: string;
    private gitItemContents: Map<string, ObservableValue<IGitItemContent | undefined>> = new Map<string, ObservableValue<IGitItemContent>>();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const repository = vcViewModelDataProviderService.getRepository()!;
        this.repositoryId = repository.id;
        this.projectId = repository.project.id;
    }

    public convertDescriptorToVersion(versionDescriptor: GitVersionDescriptor): string {
        return getVersionString(versionDescriptor);
    }

    public convertVersionToDescriptor(commitId: string): GitVersionDescriptor {
        if (commitId && (commitId[0] === "G" || commitId[0] === "P")) {
            // Parse the git version string
            return gitVersionStringToVersionDescriptor(commitId);
        }

        // If not a git version string, assume the version specifies a commit id
        return commitToVersionDescriptor(commitId);
    }

    public getFileDownloadUrl(path: string, version: GitVersionDescriptor, forceDownload: boolean = false): string {
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath;
        const url = new Uri(combineUrlPaths(hostPath, `${this.projectId}/_apis/git/repositories/${this.repositoryId}/items`));

        url.addQueryParams({
            path: path,
            ["versionDescriptor[versionOptions]"]: version.versionOptions || 0,
            ["versionDescriptor[versionType]"]: version.versionType || 0,
            ["versionDescriptor[version]"]: version.version,
            resolveLfs: true,
            $format: "octetStream",
            ["api-version"]: "5.0"
        });

        if (forceDownload) {
            url.addQueryParam("download", "true");
        }

        return url.absoluteUri;
    }

    public getFileDiff(fileDiffParameters: IFileDiffParameters): IReadonlyObservableValue<IFileDiff | undefined> {
        return this.getFileDiffEntry(fileDiffParameters).observable;
    }

    public getItemContent(path: string, version: GitVersionDescriptor): IReadonlyObservableValue<IGitItemContent | undefined> {
        const contentObservable = new ObservableValue<IGitItemContent | undefined>(undefined);
        const cacheKey = `${path}_${JSON.stringify(version)}`;

        if (isFeatureFlagEnabled(this.pageContext, "WebAccess.PullRequest.MultipleRequests", false)) {
            if (this.gitItemContents.has(cacheKey)) {
                return this.gitItemContents.get(cacheKey)!;
            }
    
            this.gitItemContents.set(cacheKey, contentObservable);
        }
        
        this.getItemContentPromise(path, version).then(content => {
            contentObservable.value = content;
        });
        return contentObservable;
    }

    public getItemContentPromise(path: string, version: GitVersionDescriptor): Promise<IGitItemContent> {
        const client = this.pageContext.getRestClient<IGitItemContentRestClient>(GitItemContentRestClientName);
        return client.getItemContent(this.repositoryId, path, this.projectId, version).then(
            result => {
                const item = result.item;
                const itemContent: IGitItemContent = {
                    commitId: item.commitId,
                    details: {
                        content: item.content,
                        contentTruncated: result.truncated,
                        metadata: item.contentMetadata,
                        objectId: item.objectId,
                        resourceUri: getGitResourceUri(this.repositoryId, version, path)
                    }
                };
                return itemContent;
            },
            error => {
                const itemContent: IGitItemContent = {
                    commitId: "",
                    details: {
                        content: "",
                        contentTruncated: false,
                        metadata: {} as FileContentMetadata,
                        objectId: "",
                        resourceUri: getGitResourceUri(this.repositoryId, version, path)
                    },
                    error
                };
                return itemContent;
            }
        );
    }

    public getObjectId(itemModel: FileDiffItemModel): string {
        return itemModel.objectId.full;
    }
}

Services.add("IGitFilesService", { serviceFactory: GitFilesService });

/**
 * REST client to get item content honoring a 206 (Partial Content) response to indicate that the content was truncated
 */
interface IGitItemContentRestClient extends IVssRestClient {
    getItemContent(
        repositoryId: string,
        path: string,
        project: string,
        versionDescriptor: GitVersionDescriptor
    ): Promise<{ item: GitItem; truncated: boolean }>;
}

class GitItemContentRestClient extends RestClientBase implements IGitItemContentRestClient {
    public async getItemContent(
        repositoryId: string,
        path: string,
        project: string,
        versionDescriptor: GitVersionDescriptor
    ): Promise<{ item: GitItem; truncated: boolean }> {
        const response = await this.beginRequest<Response>({
            apiVersion: "5.0",
            queryParams: {
                path: path,
                recursionLevel: VersionControlRecursionType.None,
                includeContentMetadata: true,
                versionDescriptor: versionDescriptor,
                includeContent: true,
                resolveLfs: true
            },
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/Items/{*path}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            returnRawResponse: true
        });

        return {
            item: (await response.text().then(deserializeVssJsonObject)) as GitItem,
            truncated: response.status === 206
        };
    }
}

const GitItemContentRestClientName = "Repos.Files.Git.IGitItemContentRestClient";

RestClients.add(GitItemContentRestClientName, {
    factory: GitItemContentRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
